import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';
import { VersionRange } from '../core/version-range.js';
import {
  EnumType, findValidEnumValuesFromList, fromEnumAsString, toEnumFromStringOrNull,
} from '../../serialization/enum-utils.js';
import { Dayjs } from 'dayjs';
import { Duration } from 'dayjs/plugin/duration.js';

export enum UnknownDataModelTypePolicy {
  Throw, Log, Ignore,
}

export type SerializedData = {[field: string]: unknown}
export type BinaryBlob = unknown;

export interface IBinaryBlobConverter {
  toBinaryBlob(bytes: Uint8Array): BinaryBlob;
  fromBinaryBlob(blob: BinaryBlob): Uint8Array;
  toBase64(bytes: Uint8Array): string;
  fromBase64(str: string): Uint8Array;
}

export abstract class SerializationContext {
  abstract get unknownTypePolicy(): UnknownDataModelTypePolicy;

  abstract get storage(): SerializationStorage;

  abstract generateUniqueId(options?: {length: number}): string;

  abstract isSerializedData(map: SerializedData): boolean;

  abstract toBinaryBlob(bytes: Uint8Array): BinaryBlob;
  abstract fromBinaryBlob(blob: BinaryBlob): Uint8Array;
  abstract toBase64(bytes: Uint8Array): string;
  abstract fromBase64(str: string): Uint8Array;

  abstract toData<T extends DataModel>(model: T): SerializedData;
  abstract toDataOrNull<T extends DataModel>(model: T | null): SerializedData | null;
  abstract fromData<T extends DataModel>(map: SerializedData): T;
  abstract fromDataOrNull<T extends DataModel>(map: SerializedData | null): T | null;
  abstract fromDataOrNullOrUndefined<T extends DataModel>(map: SerializedData | null | undefined): T | null | undefined;

  abstract encodeDate(date: Dayjs): number;
  abstract encodeDateOrNull(date: Dayjs | null): number | null;
  abstract decodeDate(encodedValue: number): Dayjs;
  abstract decodeDateOrNull(encodedValue: number | null): Dayjs | null;

  abstract encodeDuration(duration: Duration): number;
  abstract encodeDurationOrNull(duration: Duration | null): number | null;
  abstract decodeDuration(encodedValue: number): Duration;
  abstract decodeDurationOrNull(encodedValue: number | null): Duration | null;
}

export class SerializationStorage {
  private readonly _data: Map<string, unknown> = new Map<string, unknown>();

  has(key: string): boolean {
    return this._data.has(key);
  }

  remove(key: string): void {
    this._data.delete(key);
  }

  set(key: string, value: unknown): void {
    this._data.set(key, value);
  }

  get<T>(key: string, defValue: T | null = null): T | null {
    const value = (this._data.get(key) ?? null) as T | null;
    return value ?? defValue;
  }
}

export abstract class ReadonlyDataModel implements ICloneable, IDataComparable {
    abstract clone(): this

    abstract isSame(other: this): boolean

    /**
     * Current version of this data model. The version can be used
     * to implement data versioning.
     */
    get $schemaVersion(): VersionRange {
      return VersionRange.exactly(1);
    }

    /**
     *
     * A unique ID that identifies this data model class. The unique ID is used
     * to select a builder and deserialize the JSON data to the correct type.
     */
    abstract get $typeId(): string
}

export abstract class DataModel extends ReadonlyDataModel {
  asOptional(): this | null {
    return this as this | null;
  }

  /**
   * Converts the data model to a JSON dictionary.
   */
  abstract toMap(context: SerializationContext): SerializedData
}

export abstract class DataModelBuilder<T extends DataModel> {
    /**
     * Builds a new data model instance.
     */
    abstract build(context: SerializationContext, map: SerializedData): T

    // Injects custom top level fields in the map that can not be filtered in the queries otherwise
    // (ex.: fields of a list item).
    optimizeForQuery(
        context: SerializationContext,
        model: T,
    ): SerializedData | null {
      return null;
    }

    // Migrates the data so it is compatible with older versions.
    migrateFromCurrentSchemaVersion(
        context: SerializationContext,
        model: T,
        map: SerializedData,
    ): SerializedData {
      return map;
    }

    // Migrates the data so it is compatible with the current version of the app.
    migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
      return map;
    }

    // Ensures that enum value will fallback to defValue if it can not be
    // deserialized.
    //
    // See also: [migrateEnumOrNull].
    migrateEnum<T, E extends EnumType>(map: SerializedData, key: string, enumType: E, defValue: T): void {
      const enumValue = toEnumFromStringOrNull(MapUtils.getOrNull<string>(map, key), enumType) ?? defValue;
      map[key] = fromEnumAsString(enumValue);
    }

    // Same as [migrateEnum] but skips `null` values.
    // [defValue] is used only if the value is not `null` or `undefined`.
    //
    // See also: [migrateEnum].
    migrateEnumOrNull<T, E extends EnumType>(map: SerializedData, key: string, enumType: E, defValue: T): void {
      const rawValue = MapUtils.getOrNull<string>(map, key);
      if(isNullOrUndefined(rawValue)) {
        return;
      }

      const enumValue = toEnumFromStringOrNull(rawValue, enumType) ?? defValue;
      map[key] = fromEnumAsString(enumValue);
    }

    // Takes a list of serialized enum values of type {@link E}
    // and keeps only the known values.
    migrateEnumList<E extends EnumType>(map: SerializedData, key: string, enumType: E): void {
      const rawList = MapUtils.getListOrNull(map, key, (x) => x);
      if(isNullOrUndefined(rawList)) {
        return;
      }

      const values = findValidEnumValuesFromList(rawList, enumType);
      map[key] = values.map((e) => fromEnumAsString(e));
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onBeforeDeserialization(context: SerializationContext, map: SerializedData): void {}

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onBeforeSerialization(context: SerializationContext, model: T): void {}
}
